// import { Method, Options, PathHelper, request, UrlOptions } from "@js-from-routes/inertia";

import { formatUrl, UrlOptions } from "@js-from-routes/core";
import axios, { AxiosResponse } from "axios";
import { http, Method, VisitOptions } from "@inertiajs/core";

type RequestMethod = (url: string, config: VisitOptions) => Promise<any>;

interface FormHelper {
  get: RequestMethod;
  delete: RequestMethod;
  post: RequestMethod;
  put: RequestMethod;
  patch: RequestMethod;
}

/**
 * Options that can be passed to the request method.
 */
interface RequestOptions extends VisitOptions {
  params?: UrlOptions;
  data?: any;
  form?: FormHelper;
}

type Options = RequestOptions | UrlOptions;

interface PathHelper {
  <T = any>(options?: Options): Promise<T>;
  path: (params?: UrlOptions) => string;
  pathTemplate: string;
  httpMethod: Method;
}

/**
 * Returns true if the object is an Inertia.js form helper.
 */
function isFormHelper(val: any, method: Method): val is FormHelper {
  // eslint-disable-next-line no-prototype-builtins
  return val?.hasOwnProperty("data") && val[method];
}

async function request(_method: Method, url: string, options: Options = {}) {
  const { params = options.data || options, data, form = data, ...otherOptions } = options;

  const config = otherOptions as VisitOptions;
  const method = (options.method || _method).toLowerCase() as Method;
  url = formatUrl(url, params);

  if (isFormHelper(form, method)) return form[method](url, config);

  const args = method === "delete" ? [{ ...options, data }] : [data, options];
  return http.getClient().request({
    method,
    url,
    ...args,
  });
}

function getCurrentLocale() {
  // Grabs the current locale from the url
  let locale = window.location.pathname.split("/")[1];
  if (!["en", "es"].includes(locale)) {
    locale = "en";
  }

  return locale;
}

export type Path = PathHelper & {
  request<T = any>(options: UrlOptions): Promise<AxiosResponse<T>>;
};

export function definePathHelper(method: Method, pathTemplate: string): Path {
  const helper = <T = any>(options?: Options) =>
    request(method, pathTemplate, options) as Promise<T>;

  helper.path = (options?: UrlOptions) => {
    let locale = getCurrentLocale();
    return formatUrl(pathTemplate, { locale, ...options });
  };
  helper.pathTemplate = pathTemplate;
  helper.httpMethod = method;

  helper.request = (options?: UrlOptions) => {
    let locale = getCurrentLocale();

    return axios({
      method,
      url: formatUrl(pathTemplate, { locale, ...options }),
    });
  };

  return helper;
}
